import {
  ContactsOutlined,
  ControlOutlined,
  HomeOutlined,
  RobotOutlined,
  RocketOutlined,
} from '@ant-design/icons'
import MenuItem from 'Components/Menu/MenuItem'
import SubMenu from 'Components/Menu/SubMenu'
import {
  AuthorizationWrapperV2,
  checkAuthorizationV2,
} from 'Models/AuthorizeV2/checkAuthorizationV2'
import { useExperienceCenter } from 'Utils/hooks/useProductTourLink'
import PolicyMappings from 'Utils/policyMapping'
import { useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import AuthBaseLayout from '../AuthBaseLayout'

const experienceCenterMenus = [
  '/experience-center/demo-gallery',
  '/experience-center/interactive-demos',
  '/experience-center/product-tour',
]

const campaignBMenus = ['/campaigns-b/voice', '/campaigns-b/sms']

export interface LayoutProps {
  children?: React.ReactNode
  defaultCollapsed?: boolean
}

export const Component = ({ children, ...props }: LayoutProps) => {
  const location = useLocation()
  const navigate = useNavigate()

  const { productTourLink } = useExperienceCenter()

  const canViewClientsPage = useMemo(() => {
    return checkAuthorizationV2(
      {
        requiredPolicies: PolicyMappings.clientsViewClients,
      },
      []
    )
  }, [])

  const canViewExperienceCenterLandingPage = useMemo(() => {
    return checkAuthorizationV2(
      {
        requiredPolicies: PolicyMappings.dxcLandingPage,
        anywhere: true,
      },
      []
    )
  }, [])

  const getHomePage = useMemo(() => {
    if (canViewClientsPage) {
      return '/'
    }
    if (canViewExperienceCenterLandingPage) {
      return '/experience'
    }
    return ''
  }, [canViewClientsPage, canViewExperienceCenterLandingPage])

  useEffect(() => {
    if (location.pathname === '/' && canViewExperienceCenterLandingPage) {
      navigate(getHomePage)
    }
  }, [getHomePage, location.pathname, navigate])

  const selected: any = useMemo(() => {
    if (experienceCenterMenus.some((x) => location.pathname.includes(x))) {
      return { experienceCenter: true }
    }
    if (campaignBMenus.some((x) => location.pathname.includes(x))) {
      return { campaignsB: true }
    }
    return false
  }, [location.pathname])

  const campaignMenuItems = []
  campaignMenuItems.push(
    <MenuItem
      to={`/campaigns-b/voice`}
      selected={location?.pathname?.includes('/campaigns-b/voice')}
      subMenuItem
    >
      Voice
    </MenuItem>
  )

  campaignMenuItems.push(
    <AuthorizationWrapperV2 requiredPolicies={PolicyMappings.smsCampaignsView}>
      <MenuItem
        to={`/campaigns-b/sms`}
        selected={location?.pathname?.includes('/campaigns-b/sms')}
        subMenuItem
      >
        SMS
      </MenuItem>
    </AuthorizationWrapperV2>
  )

  // TODO remove Campaigns after prod testing
  return (
    <AuthBaseLayout
      defaultCollapsed={props?.defaultCollapsed}
      menu={[
        <AuthorizationWrapperV2
          key="campaigns"
          requiredPolicies={PolicyMappings.campaignsRootViewCampaigns}
          anywhere
        >
          <SubMenu
            key="campaigns-b"
            defaultOpen={selected?.campaignsB}
            selected={selected?.campaignsB}
            submenuKey={'campaigns-b'}
            title="Campaigns"
            icon={<ControlOutlined />}
            children={campaignMenuItems}
          />
        </AuthorizationWrapperV2>,
        canViewClientsPage && (
          <MenuItem key="clients" to={`/`} icon={<ContactsOutlined />}>
            Clients
          </MenuItem>
        ),
        <AuthorizationWrapperV2
          key={'template-manager'}
          requiredPolicies={PolicyMappings.templateStorePage}
        >
          <SubMenu
            key="template-manager"
            defaultOpen={selected?.experienceCenter}
            selected={selected?.experienceCenter}
            submenuKey={'template-manager'}
            title="Template manager"
            icon={<RobotOutlined />}
          >
            <MenuItem
              to={`/templates/voice`}
              selected={location?.pathname?.includes('/templates/voice')}
              subMenuItem
            >
              Voice templates
            </MenuItem>
            <MenuItem
              to={`/templates/sms`}
              selected={location?.pathname?.includes('/templates/sms')}
              subMenuItem
            >
              SMS templates
            </MenuItem>
            <MenuItem
              to={`/templates/email`}
              selected={location?.pathname?.includes('/templates/email')}
              subMenuItem
            >
              Email templates
            </MenuItem>
          </SubMenu>
        </AuthorizationWrapperV2>,
        canViewExperienceCenterLandingPage && (
          <MenuItem key="experience" to={`/experience`} icon={<HomeOutlined />}>
            Home
          </MenuItem>
        ),
        <AuthorizationWrapperV2 key={'experience-center'} requiredPolicies={PolicyMappings.dxcView}>
          <SubMenu
            key="experience-center"
            defaultOpen={selected?.experienceCenter}
            selected={selected?.experienceCenter}
            submenuKey={'experience-center'}
            title="Experience Center"
            icon={<RocketOutlined />}
          >
            <MenuItem
              to={`/experience-center/demo-gallery`}
              selected={location?.pathname?.includes('/experience-center/demo-gallery')}
              subMenuItem
            >
              Demo Gallery
            </MenuItem>
            <MenuItem
              to={`/experience-center/interactive-demos`}
              selected={location?.pathname?.includes('/experience-center/interactive-demos')}
              subMenuItem
            >
              Interactive Demos
            </MenuItem>
            <MenuItem
              to={`/experience-center/demo-users`}
              selected={location?.pathname?.includes('/experience-center/demo-users')}
              subMenuItem
            >
              Demos users
            </MenuItem>
            <MenuItem
              to={productTourLink}
              selected={location?.pathname?.includes('/experience-center/product-tour')}
              subMenuItem
            >
              Product Tour
            </MenuItem>
            <MenuItem
              to={`/experience-center/settings`}
              selected={location?.pathname?.includes('/experience-center/settings')}
              subMenuItem
            >
              Settings
            </MenuItem>
          </SubMenu>
        </AuthorizationWrapperV2>,
      ]}
      {...props}
    >
      <Outlet />
      {children}
    </AuthBaseLayout>
  )
}

export default Component
